import type {
  LoanSetupData,
  LoanApplicationData,
} from '@/store/slices/loanApplication.slice';

/**
 * LocalStorage/Cookie key for persisting query params
 */
export const POS_QUERY_PARAMS_STORAGE_KEY = 'pos-query-params';

/**
 * Set a cookie with the given name, value, and options
 * @param name - Cookie name
 * @param value - Cookie value
 * @param days - Expiration in days (default: 7)
 * @param sameSite - SameSite attribute (default: 'Lax')
 */
const setCookie = (
  name: string,
  value: string,
  days: number = 1,
  sameSite: 'Strict' | 'Lax' | 'None' = 'Lax'
): void => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  const sameSiteAttr =
    sameSite === 'None'
      ? `; SameSite=${sameSite}${secure}`
      : `; SameSite=${sameSite}`;
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/${sameSiteAttr}`;
};

/**
 * Loan Application State for URL building
 */
interface LoanApplicationState {
  loanSetup: LoanSetupData;
  application: LoanApplicationData;
}

/**
 * Parameters for building URL with loan application data
 */
interface BuildUrlWithLoanParamsOptions {
  /** The base URL to append query params to */
  baseUrl: string;
  /** The loan application state from Redux */
  loanApplication: LoanApplicationState;
  /** Optional access token to pass to the next page */
  token?: string;
  /** Optional refresh token to pass to the next page */
  refreshToken?: string;
  /** Optional token expiration in seconds */
  expiresIn?: number;
  /** Optional token type to pass to the next page */
  tokenType?: string;
  /** Optional session ID to pass to the next page */
  sessionId?: string;
  /** Optional role to pass to the next page (e.g., 'borrower') */
  role?: string;
}

/**
 * Build URL with loan application data as query parameters
 * Includes all information needed to populate header and footer along with selections
 * Also stores the params in localStorage for persistence across visits
 * @param options - The options object containing all parameters
 * @returns URL string with query parameters appended
 */
export const buildUrlWithLoanParams = ({
  baseUrl,
  loanApplication,
  token,
  refreshToken,
  expiresIn,
  tokenType,
  sessionId,
  role,
}: BuildUrlWithLoanParamsOptions): string => {
  const params: Record<string, string> = {};
  const authParams: Record<string, string> = {};

  const { loanSetup, application } = loanApplication;

  // Add auth data to fragment (after #)
  if (token) authParams.access_token = token;
  if (refreshToken) authParams.refresh_token = refreshToken;
  if (expiresIn !== undefined) authParams.expires_in = String(expiresIn);
  if (tokenType) authParams.token_type = tokenType;

  // Add session to query params
  if (sessionId) params.sessionId = sessionId;

  // Add loan setup data (selections)
  if (loanSetup.loanType) params.loanType = loanSetup.loanType;
  if (loanSetup.propertyAvailable)
    params.propertyAvailable = loanSetup.propertyAvailable;
  if (loanSetup.coBorrower) params.coBorrower = loanSetup.coBorrower;

  // Add loan officer basic data
  if (application.loanOfficerId)
    params.loanOfficerId = application.loanOfficerId;
  if (application.loanOfficerNmlsId)
    params.loanOfficerNmlsId = application.loanOfficerNmlsId;
  if (application.loanOfficerName)
    params.loanOfficerName = application.loanOfficerName;
  if (application.loanOfficerPhoto)
    params.loanOfficerPhoto = application.loanOfficerPhoto;

  // Add full loan officer data for header/footer (from loanOfficerData)
  const loData = application.loanOfficerData;
  if (loData) {
    if (loData.email) params.loanOfficerEmail = loData.email;
    if (loData.phone) params.loanOfficerPhone = loData.phone;
    if (loData.location) params.loanOfficerLocation = loData.location;
    if (loData.fullAddress) params.loanOfficerAddress = loData.fullAddress;
    if (loData.city) params.loanOfficerCity = loData.city;
    if (loData.state) params.loanOfficerState = loData.state;
    if (loData.zipCode) params.loanOfficerZipCode = loData.zipCode;
    if (loData.websiteUrl) params.loanOfficerWebsiteUrl = loData.websiteUrl;
    if (loData.landingPageUrl)
      params.loanOfficerLandingPageUrl = loData.landingPageUrl;
    if (loData.title) params.loanOfficerTitle = loData.title;
    if (loData.firstName) params.loanOfficerFirstName = loData.firstName;
    if (loData.lastName) params.loanOfficerLastName = loData.lastName;
  }

  // Add branch data for header/footer (when no loan officer is selected)
  const branchData = application.branchData;
  if (branchData) {
    if (branchData.branchId) params.branchId = branchData.branchId;
    if (branchData.name) params.branchName = branchData.name;
    if (branchData.address) params.branchAddress = branchData.address;
    if (branchData.city) params.branchCity = branchData.city;
    if (branchData.state) params.branchState = branchData.state;
    if (branchData.zipCode) params.branchZipCode = branchData.zipCode;
    if (branchData.phone) params.branchPhone = branchData.phone;
    if (branchData.email) params.branchEmail = branchData.email;
    if (branchData.branchNmls) params.branchNmls = branchData.branchNmls;
    if (branchData.websiteUrl) params.branchWebsiteUrl = branchData.websiteUrl;
    if (branchData.logo) params.branchLogo = branchData.logo;
    if (branchData.photo) params.branchPhoto = branchData.photo;

    // Add first branch manager data if available
    const manager = branchData.branchManagers?.[0];
    if (manager) {
      if (manager.id) params.branchManagerId = manager.id;
      if (manager.name) params.branchManagerName = manager.name;
      if (manager.email) params.branchManagerEmail = manager.email;
      if (manager.phone) params.branchManagerPhone = manager.phone;
      if (manager.nmls) params.branchManagerNmls = manager.nmls;
      if (manager.photo) params.branchManagerPhoto = manager.photo;
    }
  }

  // Add role as last query param
  if (role) params.role = role;

  const queryString = new URLSearchParams(params).toString();
  const hashString = new URLSearchParams(authParams).toString();

  // Store params in localStorage and cookie for persistence across visits
  const allParams = { ...params, ...authParams };
  if (Object.keys(allParams).length > 0) {
    const paramsJson = JSON.stringify(allParams);

    // Store in localStorage
    try {
      localStorage.setItem(POS_QUERY_PARAMS_STORAGE_KEY, paramsJson);
    } catch (error) {
      console.warn('Failed to save query params to localStorage:', error);
    }

    // Store in cookie for cross-subdomain sharing
    try {
      setCookie(POS_QUERY_PARAMS_STORAGE_KEY, paramsJson);
    } catch (error) {
      console.warn('Failed to save query params to cookie:', error);
    }
  }

  if (!queryString && !hashString) return baseUrl;

  let url = baseUrl;

  // Add query params after ?
  if (queryString) {
    const separator = baseUrl.includes('?') ? '&' : '?';
    url = `${url}${separator}${queryString}`;
  }

  // Add auth params after #
  if (hashString) {
    url = `${url}#${hashString}`;
  }

  return url;
};
